import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { searchContents } from '../lib/api'
import { cn, formatDate, contentTypeConfig } from '../lib/utils'
import {
  Search,
  X,
  Loader2,
  FileText,
  Globe,
  Image,
  Mic,
  BookOpen,
  File,
  Sparkles,
} from 'lucide-react'

const iconMap = {
  note: FileText,
  bookmark: Globe,
  image: Image,
  pdf: FileText,
  docx: FileText,
  doc: FileText,
  xlsx: FileText,
  xls: FileText,
  pptx: FileText,
  ppt: FileText,
  txt: FileText,
  epub: BookOpen,
  transcript: Mic,
  unknown: File,
}

function SearchResult({ result, onClick }) {
  const type = result.type && result.type !== 'unknown' ? result.type : 'note'
  const config = contentTypeConfig[type] || contentTypeConfig.unknown
  const Icon = iconMap[type] || File

  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-4 p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all text-left group"
    >
      <div
        className="p-2.5 rounded-xl flex-shrink-0"
        style={{ backgroundColor: `${config.color}20` }}
      >
        <Icon className="w-5 h-5" style={{ color: config.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium group-hover:text-primary transition-colors line-clamp-1">
          {result.title || 'Untitled'}
        </h3>
        {result.description && (
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
            {result.description}
          </p>
        )}
        {result.content && (
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2 bg-primary/5 px-2 py-1 rounded">
            {result.content.substring(0, 200)}...
          </p>
        )}
        <div className="flex items-center gap-2 mt-2">
          <span
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${config.color}20`, color: config.color }}
          >
            {config.label}
          </span>
          {result.score && (
            <span className="text-xs text-muted-foreground">
              {(result.score * 100).toFixed(0)}% match
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

export default function SearchPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const inputRef = useRef(null)
  
  const [query, setQuery] = useState(searchParams.get('q') || '')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    const q = searchParams.get('q')
    if (q && q !== query) {
      setQuery(q)
      performSearch(q)
    }
  }, [searchParams])

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([])
      setSearched(false)
      return
    }

    setLoading(true)
    setSearched(true)
    try {
      const res = await searchContents(searchQuery)
      console.log('Search response:', res)
      // API returns { results: { matches: [...], count: ... }, metadata: {...} }
      // Each match has: { id, score, metadata: { content_id, title, user_id, chunk_id } }
      let matches = []
      if (res.results?.matches && Array.isArray(res.results.matches)) {
        // Transform matches to flatten metadata for UI display
        matches = res.results.matches.map(match => ({
          content_id: match.metadata?.content_id || match.id,
          title: match.metadata?.title || 'Untitled',
          type: match.metadata?.type || 'unknown',
          score: match.score,
          chunk_id: match.metadata?.chunk_id,
          // Include any additional fields from enriched results
          description: match.description,
          content: match.content,
          updated_at: match.updated_at,
          ...match.metadata,
          ...match, // Spread the rest in case there are enriched fields
        }))
      } else if (Array.isArray(res.results)) {
        matches = res.results
      } else if (Array.isArray(res.contents)) {
        matches = res.contents
      }
      console.log('Processed matches:', matches.length, matches)
      setResults(matches)
    } catch (err) {
      console.error('Search failed:', err)
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (query.trim()) {
      setSearchParams({ q: query })
      performSearch(query)
    }
  }

  const clearSearch = () => {
    setQuery('')
    setResults([])
    setSearched(false)
    setSearchParams({})
    inputRef.current?.focus()
  }

  return (
    <div className="p-6 max-w-4xl mx-auto animate-fade-in">
      {/* Search header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <Search className="w-6 h-6 text-primary" />
          Search
        </h1>
        <p className="text-muted-foreground">
          Find anything in your knowledge base
        </p>
      </div>

      {/* Search input */}
      <form onSubmit={handleSearch} className="mb-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search notes, bookmarks, documents..."
            className="w-full pl-12 pr-12 py-4 rounded-2xl bg-card border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-lg"
          />
          {query && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-accent"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          )}
        </div>
      </form>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : searched ? (
        results.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground mb-4">
              Found {results.length} result{results.length !== 1 ? 's' : ''}
            </p>
            {results.map((result, index) => (
              <SearchResult
                key={`${result.content_id}-${result.chunk_id || index}`}
                result={result}
                onClick={() => navigate(`/content/${result.content_id}`)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No results found</h3>
            <p className="text-muted-foreground">
              Try different keywords or check your spelling
            </p>
          </div>
        )
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-lg font-medium mb-2">Search your knowledge</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Search across all your notes, bookmarks, documents, and more. 
            Our AI-powered search understands context and meaning.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {['Recent notes', 'AI articles', 'Project ideas'].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => {
                  setQuery(suggestion)
                  setSearchParams({ q: suggestion })
                  performSearch(suggestion)
                }}
                className="px-4 py-2 rounded-full bg-card border border-border hover:border-primary/50 text-sm transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
